import { utf16Length, IsoTimestampSchema } from "./string-validation"
import { z } from "zod"

import { createPaginatedResponseSchema } from "./api"

export const EmailNotificationStatusSchema = z.enum(["pending", "sent", "failed"])
export const EmailNotificationAudienceSchema = z.enum(["patient", "staff"])
export const EmailNotificationFailureStageSchema = z.enum(["preparation", "transport"])
export const EMAIL_NOTIFICATION_TYPES = [
	"eligibility_failure",
	"lab_kit_shipped",
	"lab_kit_delivered",
	"lab_sample_in_transit",
	"lab_results_received",
	"lab_review_patient_decision",
	"lab_review_staff_alert",
	"membership_renewal_reminder",
	"membership_renewal_payment_failed",
	"payment_receipt",
	"prescription_preparing",
	"prescription_shipped",
	"telehealth_no_slots_staff_alert",
	"telehealth_visit_scheduled",
	"telehealth_visit_cancelled",
	"telehealth_visit_access_ready",
	"telehealth_visit_access_link_refreshed",
] as const
export const EmailNotificationTypeSchema = z.enum(EMAIL_NOTIFICATION_TYPES)
export const EMAIL_NOTIFICATION_RECIPIENT_SEARCH_MAX_LENGTH = 200

export const EmailNotificationFiltersSchema = z.object({
	patientUserId: z.uuid().optional(),
	recipient: z.string().trim().min(1).apply(utf16Length("max", EMAIL_NOTIFICATION_RECIPIENT_SEARCH_MAX_LENGTH)).optional(),
	orderId: z.uuid().optional(),
	notificationType: EmailNotificationTypeSchema.optional(),
	audience: EmailNotificationAudienceSchema.optional(),
	status: EmailNotificationStatusSchema.optional(),
	startDate: IsoTimestampSchema.optional(),
	endDate: IsoTimestampSchema.optional(),
	page: z.coerce.number().int().positive().default(1),
	limit: z.coerce.number().int().positive().max(100).default(25),
}).meta({ id: "EmailNotificationFilters" })

export const EmailNotificationEntrySchema = z.object({
	id: z.uuid(),
	attemptedAt: IsoTimestampSchema,
	completedAt: IsoTimestampSchema.nullable(),
	sentAt: IsoTimestampSchema.nullable(),
	failedAt: IsoTimestampSchema.nullable(),
	status: EmailNotificationStatusSchema,
	senderEmail: z.email().nullable(),
	recipientEmail: z.email().nullable(),
	subject: z.string().nullable(),
	notificationType: EmailNotificationTypeSchema,
	audience: EmailNotificationAudienceSchema,
	orderId: z.uuid().nullable(),
	patientUserId: z.uuid().nullable(),
	failureStage: EmailNotificationFailureStageSchema.nullable(),
	failureSummary: z.string().nullable(),
}).meta({ id: "EmailNotificationEntry" })

export const EmailNotificationListResponseSchema = createPaginatedResponseSchema(EmailNotificationEntrySchema)
	.meta({ id: "EmailNotificationListResponse" })

export type EmailNotificationStatus = z.infer<typeof EmailNotificationStatusSchema>
export type EmailNotificationAudience = z.infer<typeof EmailNotificationAudienceSchema>
export type EmailNotificationFailureStage = z.infer<typeof EmailNotificationFailureStageSchema>
export type EmailNotificationType = z.infer<typeof EmailNotificationTypeSchema>
export type EmailNotificationFilters = z.infer<typeof EmailNotificationFiltersSchema>
export type EmailNotificationEntry = z.infer<typeof EmailNotificationEntrySchema>
export type EmailNotificationListResponse = z.infer<typeof EmailNotificationListResponseSchema>
